import { useState } from 'react';
import { useSubscription, PlanType } from '../../contexts/SubscriptionContext';
import toast from 'react-hot-toast';

interface PaymentFormProps {
  planId: PlanType;
  onSuccess: () => void;
  onCancel: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ 
  planId, 
  onSuccess, 
  onCancel 
}) => {
  const { subscribeToPlan, isSubscribing } = useSubscription();
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvc, setCvc] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!cardNumber || !cardName || !expiryDate || !cvc) {
      toast.error('Please fill in all payment details');
      return;
    }

    // Simulate payment processing
    try {
      await subscribeToPlan(planId, {
        cardNumber,
        cardName,
        expiryDate,
        cvc,
      });
      
      toast.success('Successfully subscribed to the plan!');
      onSuccess();
    } catch (error) {
      toast.error('Payment failed. Please try again.');
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <h2 className="text-lg font-medium text-gray-900">Payment Details</h2>
      
      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <div>
          <label htmlFor="card-name" className="block text-sm font-medium text-gray-700">
            Name on card
          </label>
          <input
            type="text"
            id="card-name"
            value={cardName}
            onChange={(e) => setCardName(e.target.value)}
            placeholder="John Smith"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        
        <div>
          <label htmlFor="card-number" className="block text-sm font-medium text-gray-700">
            Card number
          </label>
          <input
            type="text"
            id="card-number"
            value={cardNumber}
            onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))}
            placeholder="4242 4242 4242 4242"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="expiry-date" className="block text-sm font-medium text-gray-700">
              Expiry date
            </label>
            <input
              type="text"
              id="expiry-date"
              value={expiryDate}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                if (value.length > 2) {
                  setExpiryDate(`${value.slice(0, 2)}/${value.slice(2)}`);
                } else {
                  setExpiryDate(value);
                }
              }}
              placeholder="MM/YY"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div>
            <label htmlFor="cvc" className="block text-sm font-medium text-gray-700">
              CVC
            </label>
            <input
              type="text"
              id="cvc"
              value={cvc}
              onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 3))}
              placeholder="123"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
        
        <div className="flex items-center mt-6 space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubscribing}
            className={`w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
              isSubscribing ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSubscribing ? 'Processing...' : 'Pay Now'}
          </button>
        </div>
      </form>
      
      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>This is a demo. No actual payment will be processed.</p>
        <p>You can use any card details for testing.</p>
      </div>
    </div>
  );
};

export default PaymentForm;